import { useState, memo, useEffect } from "react";
import { EuiListGroup, EuiListGroupItem } from "@elastic/eui";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import moment from "moment-timezone";
import { AggregationIntervals } from "Consts";
import { getDatesParams } from "utils";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import "styles/components/chart/interval_selection.css";

export interface FDChartIntervalSelectProps {
  //currently supported when UseGlobalState=false
  disabled?: string[];
  end: string;
  onChange: (interval: string) => void;
  selected?: string;
  start: string;
  useGlobalState: boolean;
}

const FDChartIntervalSelect: React.FC<FDChartIntervalSelectProps> = ({
  useGlobalState = true,
  onChange,
  start,
  end,
  selected, //currently supported when UseGlobalState=false
  disabled = [],
}): JSX.Element => {
  const dispatch = useDispatch();
  const { DatePickerParams } = useSelector(globalFilterSelector, shallowEqual);
  const [selectedInterval, setSelectedInterval] = useState<string>(AggregationIntervals[0].id);

  const range = getDatesParams(
    useGlobalState ? DatePickerParams.start : start || DatePickerParams.start,
    useGlobalState ? DatePickerParams.end : end || DatePickerParams.end
  );
  const _end = moment(range.endDate);
  const _start = moment(range.startDate);

  useEffect(() => {
    if (selected) {
      setSelectedInterval(selected);
    }
  }, [selected]);

  const _onChange = (interval: string) => {
    setSelectedInterval(interval);
    if (useGlobalState) {
      dispatch({
        type: globalFilterTypes.SET_CURRENT_FILTER, // todo: action for date as this is confusing
        payload: {
          SelectedPeriod: interval,
        },
      });
    }

    if (onChange) {
      onChange(interval);
    }
  };

  // hide the interval which are not supported by time range
  const allowedOptions = AggregationIntervals.filter(
    (interval) => !disabled.includes(interval.id) && interval.isAllowed(_start, _end)
  );

  if (
    !allowedOptions.some((o) => o.id === selectedInterval) // period is not in the allowed options
  ) {
    _onChange("Auto");
  }

  return (
    <>
      <EuiListGroup
        css={{
          justifyContent: "center",
          padding: "2px",
        }}
        className="chart-interval"
        flush
        gutterSize="none"
        data-testid="chartIntervalSelector">
        {allowedOptions.map((interval: any, index) => {
          return (
            <EuiListGroupItem
              color="text"
              isActive={interval.label}
              key={index}
              label={interval.label}
              onClick={() => {
                _onChange(interval.id);
              }}
              size="s"
            />
          );
        })}
      </EuiListGroup>
    </>
  );
};

export default memo(FDChartIntervalSelect);
